import React, { Fragment, useState, useEffect } from 'react';
import { Card, CardBody } from 'reactstrap';
import { Breadcrumbs, H3, H4 } from '../../../AbstractElements';
import DataTable from 'react-data-table-component';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getClientUrlDataToken, postClientUrlWithToken } from '../../../Utils/restUtils';
import { showPayslip, showPayslipRole, showPayrollSummary, showPayrollSummaryRole, generatePayroll, generatePayrollRole } from '../../../CommonUrl';
import SweetAlert from 'sweetalert2';
import Transparentspinner from '../../Common/Loader/Transparentspinner';
const getYearMonth = (dateStr) => {
  if (!dateStr) return '';
  const parts = dateStr.split('-');
  if (parts.length === 3) {
    const monthStr = parts[1]; // e.g. "Jun"
    const year = parts[2];     // e.g. "2026"
    const months = {
      Jan: '01', Feb: '02', Mar: '03', Apr: '04', May: '05', Jun: '06',
      Jul: '07', Aug: '08', Sep: '09', Oct: '10', Nov: '11', Dec: '12'
    };
    const month = months[monthStr] || '01';
    return `${year}-${month}`;
  }
  return '';
};



function PayrollRuns() {
  const [runs, setRuns] = useState([]);

  const authState = useSelector((state) => state.auth);
  const navigate = useNavigate();

  const [payslips, setPayslips] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ column: '', order: '' });
  const [resetPaginationToggle, setResetPaginationToggle] = useState(false);

  const handleRunPayroll = async (month) => {
    if (!authState.apiUrl) return;

    SweetAlert.fire({
      title: 'Confirm Run Payroll?',
      text: `Are you sure you want to run payroll for ${month}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Ok',
      cancelButtonText: 'Cancel',
      reverseButtons: true
    }).then(async (result) => {
      if (result.value) {
        const payload = { month: month };
        setLoading(true);
        try {
          const endpoint = authState.userRole === 1 ? generatePayroll : generatePayrollRole;
          const response = await postClientUrlWithToken(authState.apiUrl, endpoint, payload);
          if (response && response.status === 200) {
            if (response.data.status === true) {
              toast.success(response.data.message || `Payroll run successfully initiated for ${month}!`);
              fetchPayrollSummary();
              fetchPayslips(currentPage);
            } else {
              toast.error(response.data?.message || "Failed to generate payroll");
            }
          } else {
            toast.error("Failed to generate payroll");
          }
        } catch (error) {
          toast.error("An error occurred while generating payroll");
        } finally {
          setLoading(false);
        }
      }
    });
  };

 

  const handlePayslipSelect = (action, month) => {
    if (!action) return;
    toast.info(`Action "${action}" selected for ${month} payslips.`);
  };

  const handleReport = (month) => {
    toast.info(`Downloading payroll report for ${month}...`);
  };

  const handleEditRules = () => {
    toast.info('Redirecting to Edit Rules Configuration...');
  };

  const payslipColumns = [
    {
      name: <H4>Sl.No</H4>,
      id: 'sl_no',
      selector: row => row.sl_no,
      sortable: true,
      cell: row => <span>{row.sl_no}</span>,
      width: '80px',
    },
    {
      name: <H4>Employee</H4>,
      id: 'employee',
      selector: row => row.employee,
      sortable: true,
      cell: row => <span>{row.employee}</span>,
    },
    {
      name: <H4>Period</H4>,
      id: 'period',
      selector: row => row.period,
      sortable: true,
      cell: row => <span>{row.period}</span>,
    },
    {
      name: <H4>Reg. Hours</H4>,
      id: 'reg_hours',
      selector: row => row.reg_hours,
      sortable: true,
      cell: row => <span>{row.reg_hours}</span>,
    },
    {
      name: <H4>OT Hours</H4>,
      id: 'ot_hours',
      selector: row => row.ot_hours,
      sortable: true,
      cell: row => <span>{row.ot_hours}</span>,
    },
    {
      name: <H4>Gross Pay</H4>,
      id: 'gross_pay',
      selector: row => row.gross_pay,
      sortable: true,
      cell: row => <span>{row.gross_pay}</span>,
    },
    {
      name: <H4>Deductions</H4>,
      id: 'deductions',
      selector: row => row.deductions,
      sortable: true,
      cell: row => <span>{row.deductions}</span>,
    },
    {
      name: <H4>Net Pay</H4>,
      id: 'net_pay',
      selector: row => row.net_pay,
      sortable: true,
      cell: row => <span>{row.net_pay}</span>,
    },
    {
      name: <H4>Action</H4>,
      id: 'action',
      sortable: false,
      cell: row => (
        <button
          onClick={() => {
            const formattedMonth = getYearMonth(row.start_date || row.period);
            navigate(`${process.env.PUBLIC_URL}/attendance/payroll-runs/details?month=${formattedMonth}&employee_id=${row.employee_id}`);
          }}
          className="btn btn-primary btn-xs"
          style={{ padding: '6px 12px', fontSize: '12px', borderRadius: '6px' }}
        >
          View
        </button>
      ),
      center: true,
      width: '120px',
    },
  ];




  const handleApiError = (status) => {
    if (status >= 400 && status <= 405) {
      navigate(`${process.env.PUBLIC_URL}/pages/error/error-page1`);
    } else if (status >= 500 && status <= 505) {
      navigate(`${process.env.PUBLIC_URL}/pages/error/error-page4`);
    } else {
      navigate(`${process.env.PUBLIC_URL}/pages/error/error-page3`);
    }
  };

  const fetchPayslips = async (page, size = perPage, sortBy = '', sortOrder = '') => {
    setLoading(true);
    const endpoint = authState.userRole === 1 ? showPayslip : showPayslipRole;
    const response = await getClientUrlDataToken(
      authState.apiUrl,
      `${endpoint}?page=${page}&page_length=${size}&sort_by=${sortBy}&sort_order=${sortOrder}`
    );
    if (response && response.status === true) {
      setResetPaginationToggle(!resetPaginationToggle);
      setPayslips(response.data.data || []);
      setTotalRows(response.data.total || 0);
    } else {
      handleApiError(response ? response.status : 500);
    }
    setLoading(false);
  };

  const fetchPayrollSummary = async () => {
    if (!authState.apiUrl) return;
    setLoading(true);
    try {
      const endpoint = authState.userRole === 1 ? showPayrollSummary+'?months=3' : showPayrollSummaryRole;
      const response = await getClientUrlDataToken(
        authState.apiUrl,
        endpoint
      );
      if (response && response.status === true) {
        const summaryData = response.data.data || response.data || [];
       
        setRuns(summaryData);
      } else {
        toast.error(response?.data?.message || 'Failed to fetch payroll summary');
      }
    } catch (error) {
      console.error('Error fetching payroll summary:', error);
      toast.error('An error occurred while fetching payroll summary');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authState.apiUrl) {
      fetchPayslips(1);
      fetchPayrollSummary();
    }
  }, [authState.apiUrl]);

  const handlePageChange = (page) => {
    fetchPayslips(page, perPage, sortConfig.column, sortConfig.order);
    setCurrentPage(page);
  };

  const handlePerRowsChange = async (newPerPage, page) => {
    fetchPayslips(page, newPerPage, sortConfig.column, sortConfig.order);
    setPerPage(newPerPage);
  };

  const handleSort = (column) => {
    let newOrder = 'asc';
    if (sortConfig.column === column.id) {
      newOrder = sortConfig.order === 'asc' ? 'desc' : 'asc';
    }
    setSortConfig({ column: column.id, order: newOrder });
    fetchPayslips(currentPage, perPage, column.id, newOrder);
  };

  return (
    <Fragment>
      {loading && <Transparentspinner />}
      <Breadcrumbs mainTitle="Payroll Runs" parent="Attendance" title="Payroll Runs" />

      <div className="container-fluid">
        {/* Monthly Runs Grid */}
        <div 
          style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', 
            gap: '24px', 
            marginBottom: '28px' 
          }}
        >
          {runs.map((run) => (
            <Card 
              key={run.id} 
              style={{ 
                borderRadius: '12px', 
                border: '1px solid #e5e7eb', 
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                borderTop: `4px solid ${run.topBorderColor}`,
                overflow: 'hidden'
              }}
            >
              <CardBody className="p-4">
                {/* Date & Badge */}
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <span style={{ fontSize: '13px', fontWeight: '500', color: '#6b7280' }}>
                    {run.period}
                  </span>
                  <span style={{
                    backgroundColor: run.status === 'Draft' ? '#f3f4f6' : '#dbeafe',
                    color: run.status === 'Draft' ? '#4b5563' : '#1d4ed8',
                    fontSize: '12px',
                    padding: '4px 12px',
                    borderRadius: '12px',
                    fontWeight: '600',
                  }}>
                    {run.status}
                  </span>
                </div>

                {/* Amount */}
                <div className="d-flex align-items-baseline gap-1 mb-1">
                  <span style={{ fontSize: '28px', fontWeight: '700', color: '#111827' }}>
                    {run.amount}
                  </span>
                </div>
                <div className="mb-4" style={{ fontSize: '13px', color: '#6b7280', fontWeight: '500' }}>
                  {run.amount_type}
                </div>

                {/* Divider */}
                <div style={{ borderTop: '1px solid #f3f4f6', margin: '20px 0' }} />

                {/* Metrics */}
                <div className="d-flex justify-content-between mb-4">
                  <div>
                    <div style={{ fontSize: '18px', fontWeight: '700', color: '#111827' }}>{run.employee_count}</div>
                    <div style={{ fontSize: '12px', color: '#9ca3af', fontWeight: '500' }}>Employees</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '18px', fontWeight: '700', color: '#111827' }}>{run.total_hours}</div>
                    <div style={{ fontSize: '12px', color: '#9ca3af', fontWeight: '500' }}>Total hours</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '18px', fontWeight: '700', color: '#111827' }}>{run.overtime_hours}</div>
                    <div style={{ fontSize: '12px', color: '#9ca3af', fontWeight: '500' }}>Overtime</div>
                  </div>
                </div>

                {/* Actions */}
                {run.status === 'draft' ? (
                  <div className="d-flex gap-3">
                    <button 
                      onClick={() => handleRunPayroll(run.month)}
                      style={{
                        flex: 1,
                        backgroundColor: '#059669',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '10px 16px',
                        fontWeight: '600',
                        fontSize: '14px',
                        cursor: 'pointer',
                        transition: 'background-color 0.2s',
                      }}
                      onMouseOver={(e) => e.target.style.backgroundColor = '#047857'}
                      onMouseOut={(e) => e.target.style.backgroundColor = '#059669'}
                    >
                      Run Payroll
                    </button>
                    {/* <button 
                      onClick={() => handlePreview(run.month)}
                      style={{
                        backgroundColor: '#ffffff',
                        color: '#374151',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        padding: '10px 20px',
                        fontWeight: '600',
                        fontSize: '14px',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                      }}
                      onMouseOver={(e) => {
                        e.target.style.backgroundColor = '#f9fafb';
                        e.target.style.borderColor = '#d1d5db';
                      }}
                      onMouseOut={(e) => {
                        e.target.style.backgroundColor = '#ffffff';
                        e.target.style.borderColor = '#e5e7eb';
                      }}
                    >
                      Preview
                    </button> */}
                  </div>
                ) : (
                  <div className='d-grid'>
                    {/* <div style={{ flex: 1, position: 'relative' }}>
                      <select 
                        onChange={(e) => {
                          handlePayslipSelect(e.target.value, run.month);
                          e.target.value = ""; // Reset dropdown selection after toast
                        }}
                        defaultValue=""
                        style={{
                          width: '100%',
                          backgroundColor: '#ffffff',
                          color: '#374151',
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          padding: '10px 16px',
                          paddingRight: '32px',
                          fontWeight: '500',
                          fontSize: '14px',
                          cursor: 'pointer',
                          outline: 'none',
                          appearance: 'none',
                          WebkitAppearance: 'none',
                          MozAppearance: 'none',
                        }}
                      >
                        <option value="" disabled>Payslips</option>
                        <option value="View Payslips">View Payslips</option>
                        <option value="Download All">Download All</option>
                        <option value="Email All">Email All</option>
                      </select>
                      <div style={{
                        position: 'absolute',
                        right: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        pointerEvents: 'none',
                        color: '#6b7280',
                        fontSize: '10px'
                      }}>
                        ▼
                      </div>
                    </div>
                    <button 
                      onClick={() => handleReport(run.month)}
                      style={{
                        backgroundColor: '#ffffff',
                        color: '#374151',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        padding: '10px 20px',
                        fontWeight: '600',
                        fontSize: '14px',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                      }}
                      onMouseOver={(e) => {
                        e.target.style.backgroundColor = '#f9fafb';
                        e.target.style.borderColor = '#d1d5db';
                      }}
                      onMouseOut={(e) => {
                        e.target.style.backgroundColor = '#ffffff';
                        e.target.style.borderColor = '#e5e7eb';
                      }}
                    >
                      Report
                    </button> */}
                   <button 
                      // onClick={() => handleReport(run.month)}
                      style={{
                        backgroundColor: '#ffffff',
                        color: '#374151',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        padding: '10px 20px',
                        fontWeight: '600',
                        fontSize: '14px',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                      }}
                      onMouseOver={(e) => {
                        e.target.style.backgroundColor = '#f9fafb';
                        e.target.style.borderColor = '#d1d5db';
                      }}
                      onMouseOut={(e) => {
                        e.target.style.backgroundColor = '#ffffff';
                        e.target.style.borderColor = '#e5e7eb';
                      }}
                    >
                      ✓ Payroll Processed
                    </button> 
                  </div>
                )}
              </CardBody>
            </Card>
          ))}
        </div>

        {/* Rules Configuration Table */}
        <Card style={{ borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', marginBottom: '30px' }}>
          <div className="d-flex justify-content-between align-items-center px-4 py-3" style={{ borderBottom: '1px solid #f3f4f6' }}>
            <H3 >
              Payslips
            </H3>
            
          </div>
          <CardBody>
            <DataTable
              columns={payslipColumns}
              data={payslips}
              onSort={handleSort}
              sortServer
              pagination
              paginationServer
              paginationTotalRows={totalRows}
              paginationDefaultPage={currentPage}
              onChangeRowsPerPage={handlePerRowsChange}
              onChangePage={handlePageChange}
              fixedHeader
            />
          </CardBody>
        </Card>
      </div>
    </Fragment>
  );
}

export default PayrollRuns;
